import { Trash2, Pencil, Ban } from "lucide-react";
import { EVENT_MAP, eventNumbers, eventMinute, formatTime, targetLabel, teamName, PERIOD_SHORT } from "@/lib/events";
import { teamStyle } from "@/lib/colors";

const EDITABLE = ["amarelo", "vermelho", "ca_por_exibir", "cv_por_exibir"];

const IconBtn = ({ testId, onClick, title, hover, children }) => (
  <button data-testid={testId} onClick={onClick} title={title}
    className={`tap w-10 h-10 flex items-center justify-center text-zinc-500 hover:bg-white/5 ${hover}`}>
    {children}
  </button>
);

const Row = ({ e, n, match, onDelete, onEdit, onAnnul }) => {
  const t = EVENT_MAP[e.type];
  const sub = e.type === "substituicao" && e.team;
  const badge = sub ? teamStyle(match[e.team]) : { background: t.color, color: t.text };
  const detail = [e.team && teamName(match, e.team), e.target && targetLabel(e), e.zone && `Zona ${e.zone}`].filter(Boolean).join(" · ") || "—";
  return (
    <li data-testid={`timeline-item-${e.id}`} className="rise flex items-start gap-3 sm:gap-4 py-3 pl-3"
      style={sub ? { boxShadow: `inset 4px 0 0 ${badge.background}` } : undefined}>
      <div className="w-14 sm:w-24 shrink-0">
        <span data-testid={`timeline-minute-${e.id}`} className="block font-display text-xl sm:text-2xl font-bold tabular-nums leading-none">{eventMinute(e, match.half2_reset)}</span>
        <span className="text-[11px] sm:text-xs text-zinc-500 tabular-nums">{formatTime(e.seconds)} · {PERIOD_SHORT[e.period || 1]}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span data-testid={`timeline-number-${e.id}`} className="shrink-0 w-7 h-7 rounded-full bg-white/10 text-xs font-bold flex items-center justify-center tabular-nums">{n}</span>
          <span data-testid={`timeline-badge-${e.id}`} className="shrink-0 px-2 py-0.5 font-display font-bold text-xs sm:text-sm text-center whitespace-nowrap border border-white/20" style={badge}>
            {t.short}
          </span>
          <p className="font-semibold truncate">{t.label}</p>
        </div>
        <p className="text-sm text-zinc-400 mt-1 break-words">{detail}</p>
        {e.note && <p data-testid={`timeline-note-${e.id}`} className="text-sm text-zinc-300 italic mt-0.5 break-words">“{e.note}”</p>}
      </div>
      <div className="flex shrink-0 -mr-2">
        {onAnnul && e.type === "vermelho" && e.team && (
          <button data-testid={`timeline-annul-${e.id}`} onClick={() => onAnnul(e)} title="Anular expulsão"
            className="tap h-9 px-2 mt-1 mr-1 border border-[#FF3B30]/50 text-[#FF3B30] text-xs font-semibold flex items-center gap-1 hover:bg-[#FF3B30] hover:text-white">
            <Ban size={14} /> <span className="hidden sm:inline">Anular</span>
          </button>
        )}
        {onEdit && EDITABLE.includes(e.type) && (
          <IconBtn testId={`timeline-edit-${e.id}`} onClick={() => onEdit(e)} title="Alterar jogador" hover="hover:text-[#CCFF00]"><Pencil size={18} /></IconBtn>
        )}
        {onDelete && (
          <IconBtn testId={`timeline-delete-${e.id}`} onClick={() => onDelete(e.id)} title="Eliminar" hover="hover:text-[#FF3B30]"><Trash2 size={18} /></IconBtn>
        )}
      </div>
    </li>
  );
};

export const Timeline = ({ match, onDelete, onEdit, onAnnul }) => {
  const events = [...match.events].sort((a, b) => b.period - a.period || b.seconds - a.seconds);
  if (!events.length)
    return <p data-testid="timeline-empty" className="text-zinc-500 text-sm py-8">Ainda sem eventos registados.</p>;
  const nums = eventNumbers(match);
  return (
    <ul data-testid="timeline-list" className="divide-y divide-white/10 border-y border-white/10">
      {events.map((e) => <Row key={e.id} e={e} n={nums[e.id]} match={match} onDelete={onDelete} onEdit={onEdit} onAnnul={onAnnul} />)}
    </ul>
  );
};
